import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Clock, Phone, Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';

const ScheduleIndicator = ({ compact = false, className }) => {
  const { getCurrentScheduleStatus, theme } = useTheme();
  const [status, setStatus] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    setStatus(getCurrentScheduleStatus());
    
    // Update every minute
    const interval = setInterval(() => {
      setCurrentTime(new Date());
      setStatus(getCurrentScheduleStatus());
    }, 60000);
    
    return () => clearInterval(interval);
  }, [theme.schedule]);

  if (!theme.schedule?.enabled || !status) return null;

  if (compact) {
    return (
      <div 
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
          status.isOpen 
            ? "bg-green-100 text-green-700" 
            : "bg-red-100 text-red-700",
          className
        )}
        data-testid="schedule-indicator"
      >
        <div className={cn(
          "w-2 h-2 rounded-full",
          status.isOpen ? "bg-green-500 animate-pulse" : "bg-red-500"
        )} />
        <span>{status.isOpen ? 'Ouvert' : 'Fermé'}</span>
      </div>
    );
  }

  return (
    <div 
      className={cn(
        "flex items-center gap-3 px-4 py-2 rounded-xl",
        status.isOpen 
          ? "bg-green-50 border border-green-200" 
          : status.isSpecial 
            ? "bg-[var(--color-alert-light)] border border-[var(--color-alert)]"
            : "bg-red-50 border border-red-200",
        className
      )}
      data-testid="schedule-indicator-full"
    >
      <div className={cn(
        "w-10 h-10 rounded-full flex items-center justify-center",
        status.isOpen ? "bg-green-100" : status.isSpecial ? "bg-[var(--color-alert)]/20" : "bg-red-100"
      )}>
        {status.isSpecial ? (
          <Calendar className={cn(
            "w-5 h-5",
            status.isOpen ? "text-green-600" : "text-[var(--color-alert)]"
          )} />
        ) : (
          <Clock className={cn(
            "w-5 h-5",
            status.isOpen ? "text-green-600" : "text-red-600"
          )} />
        )}
      </div>
      
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-2 h-2 rounded-full",
            status.isOpen ? "bg-green-500 animate-pulse" : "bg-red-500"
          )} />
          <span className={cn(
            "font-semibold",
            status.isOpen ? "text-green-700" : status.isSpecial ? "text-[var(--color-alert)]" : "text-red-700"
          )}>
            {status.isOpen ? 'Ouvert' : 'Fermé'}
          </span>
        </div>
        <p className="text-sm text-[#64748b]">{status.message}</p>
      </div>
      
      {!status.isOpen && status.nextOpen && (
        <div className="text-right">
          <p className="text-xs text-[#64748b]">Prochaine ouverture</p>
          <p className="text-sm font-medium text-[var(--color-primary)]">{status.nextOpen}</p>
        </div>
      )}
    </div>
  );
};

export default ScheduleIndicator;
